import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../../theme/webLayoutWidths';

import { FlatList, Platform, StyleSheet, View } from 'react-native';
import { useTheme } from '../../../theme';
import { useAppDispatch, useAppSelector } from '../../../redux/helper';
import useBackofficeAxios from '../../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../../hooks/useBackofficeOwnerAuth';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../../navigation/AppStack';
import BackOfficeOrderRow from '../../../components/backoffice/orders/BackOfficeOrderRow';
import { replaceCurrencyString, s, v } from '../../../utils/libs';
import {
  addCompletedOrders,
  clearCompleted,
  setOrdersError,
  setOrdersLoading,
  setTableSearchResults,
  setTableSearchPagination,
} from '../../../redux/backoffice/ordersSlice';
import {
  getCompletedOrders,
  searchOrderByReceiptId,
} from '../../../api/backofficeOrders';
import { useBackOfficeOrdersFilter } from '../../../Context/BackOfficeOrdersFilterContext';
import AppSkeleton from '../../../components/Reusables/AppSkeleton';

type BackOfficeCompletedOrdersProps = {
  refreshVersion?: number;
};

const BackOfficeCompletedOrders: React.FC<BackOfficeCompletedOrdersProps> = ({
  refreshVersion = 0,
}) => {
  const { theme } = useTheme();
  const styles = createStyles(theme);
  const completed = useAppSelector(s => s.backofficeOrders.completed);
  const tableSearch = useAppSelector(s => s.backofficeOrders.tableSearch);
  const tableSearchTerm = useAppSelector(
    s => s.backofficeOrders.tableSearchTerm,
  );
  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const locationVersion = useAppSelector(s => s.location.locationVersion);
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const dispatch = useAppDispatch();
  const nav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { startISO, endISO } = useBackOfficeOrdersFilter();
  const [refreshing, setRefreshing] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const requestSeqRef = useRef(0);
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => replaceCurrencyString('ksh', currency),
    [currency],
  );

  const limit = useMemo(
    () => Number(completed?.perPage || completed?.limit || 100),
    [completed?.perPage, completed?.limit],
  );

  const isSearching = useMemo(
    () => Boolean(String(tableSearchTerm || '').trim().length > 0),
    [tableSearchTerm],
  );

  const searchDocs = useMemo(
    () =>
      (tableSearch?.docs || []).filter(
        (o: any) =>
          String(o?.bp_order_master_order_status || '').toLowerCase() ===
          'completed',
      ),
    [tableSearch?.docs],
  );

  const defaultPage = useMemo(
    () => ({
      docs: [],
      hasNextPage: false,
      hasPrevPage: false,
      limit: 100,
      nextPage: 1,
      page: 1,
      perPage: 100,
      totalDocs: 0,
      totalPages: 0,
    }),
    [],
  );

  const safeTableSearch = (tableSearch as any) ?? (defaultPage as any);
  const safeCompleted = (completed as any) ?? (defaultPage as any);

  const normalizePaginated = useCallback(
    (payload: any) => ({
      ...payload,
      perPage:
        typeof payload?.perPage === 'number'
          ? payload.perPage
          : Number(payload?.limit ?? 100),
      docs: Array.isArray(payload?.docs) ? payload.docs : [],
    }),
    [],
  );

  const normalizeSearchPaginated = useCallback(
    (payload: any, page: number) => ({
      docs: Array.isArray(payload?.docs)
        ? payload.docs
        : Array.isArray(payload?.data)
          ? payload.data
          : [],
      hasNextPage: Boolean(
        payload?.hasNextPage ??
          (payload?.page ?? page) < (payload?.totalPages ?? 0),
      ),
      hasPrevPage: Boolean(payload?.hasPrevPage ?? (payload?.page ?? page) > 1),
      limit: Number(payload?.limit ?? payload?.perPage ?? 100),
      nextPage: Number(
        payload?.nextPage ??
          ((payload?.page ?? page) < (payload?.totalPages ?? 0)
            ? (payload?.page ?? page) + 1
            : (payload?.page ?? page)),
      ),
      page: Number(payload?.page ?? page),
      perPage: Number(payload?.perPage ?? payload?.limit ?? 100),
      totalDocs: Number(payload?.totalDocs ?? payload?.total ?? 0),
      totalPages: Number(
        payload?.totalPages ??
          Math.max(
            1,
            Math.ceil(
              Number(payload?.totalDocs ?? payload?.total ?? 0) /
                Number(payload?.perPage ?? payload?.limit ?? 100),
            ),
          ),
      ),
    }),
    [],
  );

  const fetchSearchPage = useCallback(
    async (page: number) => {
      if (!owner?.accessToken || !ownerId || !tableSearchTerm || localLoading)
        return;
      const seq = ++requestSeqRef.current;
      try {
        setLocalLoading(true);
        dispatch(setOrdersLoading(true));
        dispatch(setOrdersError(null));
        const res = await searchOrderByReceiptId(
          axiosBackoffice as any,
          ownerId,
          page,
          String(tableSearchTerm),
          { accessToken: owner?.accessToken },
        );
        console.log('fetchSearchPageRes', res);
        if (res?.status === 200 && seq === requestSeqRef.current) {
          const payloadRaw = (res as any)?.data?.data ?? (res as any)?.data;
          const normalized = normalizeSearchPaginated(payloadRaw, page);
          dispatch(setTableSearchResults(normalized as any));
          dispatch(
            setTableSearchPagination({
              page: normalized.page,
              perPage: normalized.perPage,
            }),
          );
        }
      } catch (e: any) {
        if (seq === requestSeqRef.current)
          dispatch(setOrdersError(e?.message || 'Failed to search orders'));
      } finally {
        if (seq === requestSeqRef.current) {
          setLocalLoading(false);
          dispatch(setOrdersLoading(false));
        }
      }
    },
    [
      owner?.accessToken,
      ownerId,
      tableSearchTerm,
      localLoading,
      axiosBackoffice,
      dispatch,
      normalizeSearchPaginated,
    ],
  );

  const fetchPage = useCallback(
    async (page: number, clearFirst: boolean = false) => {
      if (!owner?.accessToken || !ownerId || localLoading) return;
      const seq = ++requestSeqRef.current;
      try {
        setLocalLoading(true);
        dispatch(setOrdersLoading(true));
        dispatch(setOrdersError(null));
        if (clearFirst) dispatch(clearCompleted());
        const res = await getCompletedOrders(
          axiosBackoffice as any,
          ownerId,
          page,
          limit,
          startISO,
          endISO,
          { accessToken: owner?.accessToken },
        );
        if (res?.status === 200 && seq === requestSeqRef.current) {
          const payload = normalizePaginated(res?.data?.data ?? res.data);
          dispatch(addCompletedOrders(payload));
        }
      } catch (e: any) {
        if (seq === requestSeqRef.current)
          dispatch(setOrdersError(e?.message || 'Failed to load orders'));
      } finally {
        if (seq === requestSeqRef.current) {
          setLocalLoading(false);
          dispatch(setOrdersLoading(false));
        }
      }
    },
    [
      owner?.accessToken,
      ownerId,
      localLoading,
      axiosBackoffice,
      limit,
      startISO,
      endISO,
      dispatch,
      normalizePaginated,
    ],
  );

  useEffect(() => {
    if (isSearching) return;
    fetchPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startISO, endISO, isSearching, locationVersion, refreshVersion]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    if (isSearching) {
      fetchSearchPage(1).finally(() => setRefreshing(false));
    } else {
      fetchPage(1, true).finally(() => setRefreshing(false));
    }
  }, [fetchPage, fetchSearchPage, isSearching]);

  const onEndReached = useCallback(() => {
    if (isSearching) {
      if (
        safeTableSearch.hasNextPage &&
        safeTableSearch.docs.length < safeTableSearch.totalDocs
      ) {
        fetchSearchPage(safeTableSearch.nextPage);
      }
      return;
    }
    if (
      safeCompleted.hasNextPage &&
      safeCompleted.docs.length < safeCompleted.totalDocs
    ) {
      fetchPage(safeCompleted.nextPage);
    }
  }, [
    isSearching,
    safeTableSearch.hasNextPage,
    safeTableSearch.docs.length,
    safeTableSearch.totalDocs,
    safeTableSearch.nextPage,
    fetchSearchPage,
    safeCompleted.hasNextPage,
    safeCompleted.docs.length,
    safeCompleted.totalDocs,
    safeCompleted.nextPage,
    fetchPage,
  ]);

  const handlePress = useCallback(
    (order: any) => {
      nav.navigate('BackOfficeOrderDetails', {
        orderId: String(order?.bp_order_id),
      });
    },
    [nav],
  );

  const renderItem = useCallback(
    ({ item: order, index }: { item: any; index: number }) => (
      <BackOfficeOrderRow
        order={order}
        index={index}
        currencyText={currencyText}
        onPress={handlePress}
      />
    ),
    [currencyText, handlePress],
  );

  const keyExtractor = useCallback(
    (item: any) => String(item?.bp_order_id),
    [],
  );
  const listData = isSearching ? searchDocs : completed.docs;
  const showInitialSkeleton = localLoading && listData.length === 0;
  const showFooterSkeleton = localLoading && listData.length > 0;

  return (
    <View
      style={[
        { flex: 1, backgroundColor: theme.colors.surface },
        Platform.OS === 'web' && {
          maxWidth: WEB_MAX_CONTENT_WIDTH,
          alignSelf: 'center',
          width: '100%',
        },
      ]}>
      <FlatList
        data={listData}
        keyExtractor={keyExtractor}
        initialNumToRender={10}
        refreshing={refreshing}
        onEndReachedThreshold={0.8}
        onEndReached={onEndReached}
        onRefresh={onRefresh}
        renderItem={renderItem}
        ListHeaderComponent={
          showInitialSkeleton ? (
            <View style={styles.skeletonWrap}>
              <AppSkeleton mode="orders_list" count={6} />
            </View>
          ) : null
        }
        ListFooterComponent={
          showFooterSkeleton ? (
            <View style={styles.skeletonWrap}>
              <AppSkeleton mode="orders_list" count={2} />
            </View>
          ) : null
        }
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

export default BackOfficeCompletedOrders;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {},
    skeletonWrap: {
      paddingVertical: v(10),
      paddingHorizontal: s(4),
    },
  });
